import van from "../../vanjs.mjs";
import { BaseComponent } from "../baseComponent.mjs";
import {Alert} from "../elements/alert.mjs";
import {ShaderLayer} from "./shaderLayer.mjs";
import {draggable} from "../mixins/utils.mjs";

const { div, span, select, option, br, ul, li, a } = van.tags;
// Shaders to disable. These are either not configurable via UI or not meant to be used.
// A type listed here is not merely un-offered: a layer already using it has no matching
// option, so ShaderLayer suppresses its type selector entirely and shows a static label
// instead (see _buildRenderTypeSelector), and UTILITIES.changeVisualizationLayer refuses
// the switch outright. Source of truth is UTILITIES so both ends cannot drift apart.
const fallbackDisabledShaders = ["group", "interaction-debug", "time-series", "texture"];
const getDisabledShaders = () => window.UTILITIES?.NON_SWITCHABLE_SHADER_TYPES || fallbackDisabledShaders;
/**
 * ShaderSideMenu (DaisyUI)
 * Props:
 *  - shaders: Array<{ value:string, label:string }>
 *  - selectedVisualization?: string
 *  - opacity?: number   // 0..1
 *  - onShaderChange?(value)
 *  - onOpacityChange?(value)
 *  - onCacheSnapshotByName?()
 *  - onCacheSnapshotByOrder?()
 *
 * Keeps legacy element IDs:
 *   panel-images,
 *   cache-snapshot, global-opacity
 */
export class ShaderSideMenu extends BaseComponent {
    constructor(opts = undefined) {
        opts = super(opts).options;
        this.visualizations = [];
        this.visualizationSelect = null;
        this.selectedVisualization = "";
        this.shaderNodeCells = {};
        this.shaderChildrenContainers = {};
        // Shader keys that already carry a faulty-source alert in the current
        // controls generation. Reset whenever the renderer rebuilds its HTML.
        this._faultyAlertKeys = new Set();
        this.opacity = typeof opts.opacity === "number" ? opts.opacity : 1;
        this.classMap["base"] = "p-0 flex flex-col max-h-full"

        this.cb = {
            onShaderChange: opts.onShaderChange,
            onOpacityChange: opts.onOpacityChange,
            onPinChange: opts.onPinChange,
            onCacheSnapshotByName: opts.onCacheSnapshotByName,
            onCacheSnapshotByOrder: opts.onCacheSnapshotByOrder,
        };

        // dropdown state handler (DaisyUI "dropdown-open" class toggle)
        this._outsideHandler = (e) => {
            if (!this._cacheDropdownWrap) return;
            if (!this._cacheDropdownWrap.contains(e.target)) this._setCacheOpen(false);
        };
    }

    /**
     * Needs two-level init, constructor is called before viewer is opened, because this menu needs to build
     * navigator container before viewer creation, and register events after
     * @param {OpenSeadragon.Viewer} viewer
     */
    init(viewer) {
        viewer.drawer.renderer.addHandler('html-controls-created', e => {
            this._enableDragSort(viewer);

            // Fresh controls generation — DOM nodes (and thus their alerts) were
            // rebuilt, so start the faulty-alert bookkeeping clean.
            this._faultyAlertKeys = new Set();
            this._scanFaultyShaders(viewer);

            /**
             * Fired when visualization goal is set up and run, but before first rendering occurs.
             * @property visualization visualization configuration used
             * @memberof OpenSeadragon.Viewer
             * @event visualization-used
             */
            viewer.raiseEvent('visualization-used', e);
        });

        // A source can be marked faulty AFTER the controls were built (its tile
        // requests start failing mid-session). Patch the alert in without
        // waiting for a full renderer rebuild.
        viewer.addHandler('source-marked-faulty', () => this._scanFaultyShaders(viewer));

        this._refreshOrder = (node) => {
            const listItems = Array.prototype.map.call(node.children, child => child.dataset.id);
            const shouldReverse = node.dataset.reverseOrder !== "false";
            if (shouldReverse) {
                listItems.reverse();
            }

            const parentShaderId = node.dataset.parentId;
            if (parentShaderId) {
                const parentShader = viewer.drawer.renderer.getShaderLayer(parentShaderId);
                const parentConfig = parentShader?.getConfig?.();
                if (!parentConfig) {
                    console.error(`Invalid parent group id '${parentShaderId}' in _refreshOrder`);
                    return;
                }
                parentConfig.order = listItems;
                parentShader.shaderLayerOrder = [...listItems];
            } else {
                // todo no change on the navigator...
                // An EMPTY list means "no explicit order" and renders every
                // registered layer — it used to mean the opposite, "render
                // nothing", which was a bug: `[]` is truthy, so the order stayed
                // pinned at nothing however many layers were registered later,
                // and the viewer went blank. Sending `[]` to hide everything
                // will not work; hide the layers instead.
                viewer.drawer.renderer.setShaderLayerOrder(listItems);
            }
            viewer.drawer.rebuild();
        };
    }

    /**
     * Resolve a faulty-source error message for a tiled image, preferring the
     * persisted per-viewer registry (survives rebuilds / viz switches) and
     * falling back to the live source's getMetadata().error. Returns undefined
     * when the source is healthy.
     * @param {OpenSeadragon.Viewer} viewer
     * @param {OpenSeadragon.TiledImage} tiledImage
     */
    _resolveSourceError(viewer, tiledImage) {
        if (!tiledImage) return undefined;
        const src = tiledImage.source;
        const srcKey = src?.tileSourceId || src?.url || tiledImage.__xopatLoadKey;
        const registryError = viewer.__faultySources?.getError?.(srcKey);
        if (registryError) return String(registryError);
        // A missing getMetadata() no longer suppresses the warning — we simply
        // have no secondary signal beyond the registry for such a source.
        const meta = typeof src?.getMetadata === 'function' ? src.getMetadata() : undefined;
        return meta && meta.error ? String(meta.error) : undefined;
    }

    /**
     * Scan every shader layer and prepend a faulty-source alert to any layer
     * whose backing tile source is faulty. Idempotent within a controls
     * generation via {@link _faultyAlertKeys}.
     * @param {OpenSeadragon.Viewer} viewer
     */
    _scanFaultyShaders(viewer) {
        const layers = viewer.drawer.renderer.getAllShaders();
        for (let key in layers) {
            if (!layers.hasOwnProperty(key)) continue;
            if (this._faultyAlertKeys.has(key)) continue;

            const node = this.shaderNodeCells[key];
            if (!node) continue;

            const shader = layers[key];
            for (let source of shader.getConfig().tiledImages) {
                const error = this._resolveSourceError(viewer, viewer.world.getItemAt(source));
                if (error) {
                    const alert = new Alert({
                        mode: "warning",
                        title: $.t('main.shaders.faulty'),
                        description: `<code>${error}</code>`,
                        compact: true,
                        extraClasses: { margin: "mb-2" },
                    });
                    alert.prependedTo(node);
                    this._faultyAlertKeys.add(key);
                    break;
                }
            }
        }
    }

    _setCacheOpen(open) {
        if (!this._cacheDropdownWrap) return;
        this._cacheDropdownWrap.classList.toggle("dropdown-open", !!open);
        if (open) {
            document.addEventListener("click", this._outsideHandler, { capture: true });
        } else {
            document.removeEventListener("click", this._outsideHandler, { capture: true });
        }
    }

    _buildHeaderRow() {
        const noVisualizationLabel =
            $.t("main.shaders.noVisualization")
            || $.t("common.None")
            || "No visualization";
        const shaderGoalList = [
            option({ value: "" }, noVisualizationLabel),
            ...this.visualizations.map((s) => option({ value: s.value }, s.label))
        ];
        // Shader select
        this.visualizationSelect = select(
            {
                id: this.id + "-shaders",
                name: "shaders",
                class: "select select-sm w-full bg-base-200/90 border border-base-300 text-base-content text-sm font-semibold cursor-pointer",
                "aria-label": "Visualization",
                value: this.selectedVisualization,
                onchange: e => {
                    this.selectedVisualization = e.target.value;
                    this.cb.onShaderChange?.(this.selectedVisualization);
                },
                mousedown: e => {
                    if (this.childElementCount < 2) {
                        e.preventDefault();
                        // todo open the shader layer container
                        return false;
                    }
                },
                title: $.t("main.shaders.select") ?? "Select shader",
            },
            ...shaderGoalList
        );


        // todo implement using dropdown!
        this._cacheDropdownWrap = div(
            { class: "dropdown dropdown-end relative" },
            // trigger
            span(
                {
                    id: this.id + "-cache-snapshot",
                    tabindex: "0",
                    role: "button",
                    class: "ph-light ph-bookmark btn btn-ghost btn-circle btn-sm align-middle ml-1 text-base",
                    title: $.t("main.shaders.saveCookies"),
                    onclick: (e) => {
                        e.stopPropagation();
                        const open = !this._cacheDropdownWrap.classList.contains("dropdown-open");
                        this._setCacheOpen(open);
                    },
                }
            ),
            // menu
            // `menu-sm` + text-xs to match the canvas context menu
            // (ui/classes/components/contextMenu.mjs) — two short entries do not
            // need an 18rem panel of full-size rows. Padding is inline because
            // the shipped tailwind.min.css is the purged build.
            ul(
                {
                    tabindex: "0",
                    class: "dropdown-content menu menu-sm shadow bg-base-100 rounded-box z-[1] text-xs",
                    style: "min-width: 12rem; padding: 0.25rem;"
                },
                li(
                    a(
                        {
                            title: $.t("main.shaders.cacheByName"),
                            onclick: () => {
                                this.cb.onCacheSnapshotByName?.();
                                this._setCacheOpen(false);
                            },
                        },
                        // icon: sort_by_alpha
                        span({ class: "ph-light ph-sort-ascending mr-1" }),
                        $.t("main.shaders.cacheByName")
                    )
                ),
                li(
                    a(
                        {
                            title: $.t("main.shaders.cacheByOrder"),
                            onclick: () => {
                                this.cb.onCacheSnapshotByOrder?.();
                                this._setCacheOpen(false);
                            },
                        },
                        // icon: format_list_numbered
                        span({ class: "ph-light ph-list-numbers mr-1" }),
                        $.t("main.shaders.cacheByOrder")
                    )
                )
            )
        );

        return div({ class: "flex flex-row items-center gap-1" }, this.visualizationSelect, this._cacheDropdownWrap);
    }

    create() {
        // Padding lives on the sections, not on the root: the side-menu tab
        // wrapper contributes none, and every panel in the column shares the
        // annotations rhythm (`px-2 mt-1`) so their insets line up.
        // Optional images panel placeholder (kept per legacy markup)
        const panelImages = div({ id: this.id + "-panel-images", class: "px-2 mt-2" });

        const header = this._buildHeaderRow();
        this.layerContainer = div({ class: "clear-both mt-2", "data-reverse-order": "true" });
        const blendingEq = div({ id: this.id + "-blending-equation" });
        const content = div(
            { class: "select-none px-2 mt-1" },
            header,
            this.layerContainer,
            blendingEq
        );

        return div(
            { id: this.id + "-panel-shaders", class: "w-full" },
            content,
            panelImages
        );
    }

    // ----- external API -----
    _normalizeVisualizationEntry(item, index) {
        if (item && (item.value !== undefined || item.label !== undefined)) {
            return {
                value: String(item.value ?? index),
                label: String(item.label ?? item.name ?? item.value ?? $.t('main.shaders.defaultTitle'))
            };
        }

        if (item && typeof item === "object") {
            return {
                value: String(item.index ?? index),
                label: String(item.name ?? item.label ?? $.t('main.shaders.defaultTitle'))
            };
        }

        return {
            value: String(index),
            label: String(item ?? $.t('main.shaders.defaultTitle'))
        };
    }

    updateVisualizationList(shaders, selectedValue) {
        this.visualizations = (shaders || []).map((shader, index) => this._normalizeVisualizationEntry(shader, index));
        this.selectedVisualization = selectedValue !== undefined && selectedValue !== null
            ? String(selectedValue)
            : "";
        const sel = this.visualizationSelect;
        if (sel) {
            sel.innerHTML = "";
            const noVisualizationOption = document.createElement("option");
            noVisualizationOption.value = "";
            noVisualizationOption.textContent =
                $.t("main.shaders.noVisualization")
                || $.t("common.None")
                || "No visualization";
            sel.appendChild(noVisualizationOption);
            this.visualizations.forEach((s) => {
                const opt = document.createElement("option");
                opt.value = String(s.value);
                opt.textContent = s.label;
                sel.appendChild(opt);
            });
            sel.value = this.selectedVisualization;
        }
    }

    /**
     * Made with love by @fitri
     * This is a component of my ReactJS project https://codepen.io/fitri/full/oWovYj/
     *
     * Shader re-compilation and re-ordering logics
     * Modified by Jiří
     */
    _enableDragSort(viewer) {
        const bindContainer = (container) => draggable(container, item => {
            const id = item.dataset.id;
            window.DropDown.bind(item, () => {
                const currentMask = viewer.drawer.getOverriddenShaderConfig(id)?.params.use_mode;
                const clipSelected = currentMask === "clip";
                const maskEnabled = typeof currentMask === "string" && currentMask !== "show";

                return [{
                    title: $.t('main.shaders.defaultBlending'),
                }, {
                    title: maskEnabled ? $.t('main.shaders.maskDisable') : $.t('main.shaders.maskEnable'),
                    action: (selected) => UTILITIES.shaderPartSetBlendModeUIEnabled(id, !selected, viewer),
                    selected: maskEnabled
                }, {
                    title: clipSelected ? $.t('main.shaders.clipMaskOff') : $.t('main.shaders.clipMask'),
                    icon: "ph-scissors",
                    styles: "padding-right: 5px;",
                    action: (selected) => {
                        // The mode is owned by the layer config and re-read by
                        // ShaderLayer; there is no `-mode-toggle` element to
                        // stash it on (there has not been one for a while, and
                        // dereferencing it threw right here).
                        const newMode = selected ? "blend" : "clip";
                        if (!maskEnabled) {
                            UTILITIES.shaderPartSetBlendModeUIEnabled(id, true, viewer);
                        } else {
                            UTILITIES.changeModeOfLayer(id, newMode, false, viewer);
                        }
                    },
                    selected: clipSelected
                }];
            });
        }, undefined, e => {
            this._refreshOrder(e.target.parentNode);
        });

        bindContainer(this.layerContainer);
        this.layerContainer
            .querySelectorAll(".shader-group-children")
            .forEach(container => bindContainer(container));
    }

    /**
     * Refresh visualization shader order based on the UI DOM parent container child list
     * children are expected to have data-id attribute set to the shader id
     * @param node
     * @private
     */
    _refreshOrder(node) {
        console.error("called before init().");
    }
    
    clearLayers() {
        this.layerContainer.innerHTML = "";
        this.shaderNodeCells = {};
        this.shaderChildrenContainers = {};
    }

    _findShaderLayerById(shaderMap, shaderId) {
        if (!shaderMap || !shaderId) {
            return null;
        }

        for (const [id, shader] of Object.entries(shaderMap)) {
            if (!shader) {
                continue;
            }

            if (id === shaderId || shader.id === shaderId) {
                return shader;
            }

            const nested = this._findShaderLayerById(shader.shaderLayers, shaderId);
            if (nested) {
                return nested;
            }
        }

        return null;
    }

    _resolveShaderLayer(viewer, shaderLayer, htmlContext = {}) {
        const renderer = viewer?.drawer?.renderer;
        if (!renderer || !shaderLayer?.id) {
            return null;
        }

        const allShaders = renderer.getAllShaders?.() || {};

        if (!htmlContext.parentShaderId) {
            return renderer.getShaderLayer(shaderLayer.id)
                || this._findShaderLayerById(allShaders, shaderLayer.id);
        }

        const parentShader = this._findShaderLayerById(allShaders, htmlContext.parentShaderId);
        if (!parentShader?.shaderLayers) {
            return this._findShaderLayerById(allShaders, shaderLayer.id);
        }

        return parentShader.shaderLayers[shaderLayer.id]
            || this._findShaderLayerById(parentShader.shaderLayers, shaderLayer.id)
            || this._findShaderLayerById(allShaders, shaderLayer.id);
    }

    _resolveShaderConfig(shader, shaderLayer) {
        if (!shader?.getConfig) {
            return null;
        }

        let cfg = null;
        try {
            cfg = shader.getConfig(shaderLayer?.id);
        } catch (e) {
            cfg = null;
        }

        if (!cfg) {
            try {
                cfg = shader.getConfig();
            } catch (e) {
                cfg = null;
            }
        }

        return cfg;
    }
    
    createLayer(viewer, shaderLayer, shaderConfig, htmlContext = {}) {
        // map the mediator list to [{type, name}]
        const disabledShaders = getDisabledShaders();
        const availableShaders = OpenSeadragon
            .FlexRenderer

            .ShaderLayerRegistry
            .availableShaderLayers()
            .filter(s => !disabledShaders.includes(s.type()))
            .map(s => ({type: s.type(), name: s.name()}));

        // map filters if you want editable rows (optional)
        const filters = {};
        shaderConfig.params = shaderConfig.params || {};
        for (let key in OpenSeadragon.FlexRenderer.ShaderLayer.filters) {
            if (shaderConfig.params.hasOwnProperty(key)) {
                filters[key] = {
                    name: OpenSeadragon.FlexRenderer.ShaderLayer.filterNames[key],
                    value: shaderLayer.getFilterValue(key, shaderConfig.params[key])
                };
            }
        }

        const uiLayer = new ShaderLayer({
            id: `${shaderLayer.id}-shader`,
            shaderLayer,
            shaderConfig: shaderConfig,
            htmlContext,
            availableFilters: filters,
            availableShaders,
            callbacks: {
                onToggleVisible: (checked) => {
                    let shader = uiLayer.cfg;
                    if (shader) {
                        shader.visible = !!checked;
                        viewer.drawer.rebuild(0);
                    }
                },
                onChangeType: (type) =>
                    UTILITIES.changeVisualizationLayer(shaderLayer.id, type, viewer),

                // NEW: explicit mode change (no toggle, and we also keep blend mode)
                onChangeMode: (mode, blend) => {
                    const shader = this._resolveShaderLayer(viewer, shaderLayer, htmlContext);
                    if (!shader) {
                        console.error(`Invalid layer id '${shaderLayer.id}' in onChangeMode`);
                        return;
                    }
                    const cfg = this._resolveShaderConfig(shader, shaderLayer);
                    if (!cfg) {
                        console.error(`Invalid config for layer id '${shaderLayer.id}' in onChangeMode`);
                        return;
                    }

                    cfg.params = cfg.params || {};
                    cfg.params.use_mode = mode;
                    if (blend) {
                        cfg.params.use_blend = blend;
                    }

                    shader.resetMode(cfg.params);
                    viewer.drawer.rebuild(0);
                },

                // NEW: explicit blend change; keep current mode
                onChangeBlend: (mode, blend) => {
                    const shader = this._resolveShaderLayer(viewer, shaderLayer, htmlContext);
                    if (!shader) {
                        console.error(`Invalid layer id '${shaderLayer.id}' in onChangeBlend`);
                        return;
                    }
                    const cfg = this._resolveShaderConfig(shader, shaderLayer);
                    if (!cfg) {
                        console.error(`Invalid config for layer id '${shaderLayer.id}' in onChangeBlend`);
                        return;
                    }

                    cfg.params = cfg.params || {};
                    if (mode) {
                        cfg.params.use_mode = mode;   // keep in sync
                    }
                    cfg.params.use_blend = blend;

                    shader.resetMode(cfg.params);
                    viewer.drawer.rebuild(0);
                },

                onSetFilter: (key, val) =>
                    UTILITIES.setFilterOfLayer(shaderLayer.id, key, val, viewer),
                onClearCache: () =>
                    UTILITIES.clearShaderCache(shaderLayer.id, viewer),
                onReorder: (dir) => {
                    const node = this.shaderNodeCells[shaderLayer.id];
                    const parent = node?.parentElement;
                    if (!parent || !node) return;

                    if (dir === "up") {
                        const prev = node.previousElementSibling;
                        if (prev) parent.insertBefore(node, prev);
                    } else if (dir === "down") {
                        const next = node.nextElementSibling;
                        if (next) parent.insertBefore(next, node);
                    }

                    this._refreshOrder(parent);
                },
            }
        });

        const node = uiLayer.create();

        // Idempotent by shader id: if this id already has a row (e.g. two
        // renderer rebuild cycles both emit a build without an intervening
        // clearLayers(), as the playground's open()+overrideConfigureAll race
        // does), replace the stale row instead of prepending a duplicate.
        const existing = this.shaderNodeCells[shaderLayer.id];
        if (existing) {
            existing.remove();
            delete this.shaderChildrenContainers[shaderLayer.id];
        }

        const parentContainer = htmlContext.parentShaderId
            ? this.shaderChildrenContainers[htmlContext.parentShaderId]
            : this.layerContainer;

        if (parentContainer) {
            if (htmlContext.parentShaderId) {
                parentContainer.prepend(node);
            } else {
                this.layerContainer.prepend(node);
            }
        } else {
            this.layerContainer.prepend(node);
        }

        if (htmlContext.hasChildren) {
            this.shaderChildrenContainers[shaderLayer.id] = document.getElementById(uiLayer.getChildrenContainerId());
        }

        this.shaderNodeCells[shaderLayer.id] = node;
    }

    disconnected() {
        document.removeEventListener("click", this._outsideHandler, { capture: true });
    }
}
